import { CalendarModel, CalendarRepository, CalendarSendHelper, GratefulStatus, StatusCalendar } from '@src/core';
import { DateHelper } from '@src/core/helpers/date.helper';
import { Unsubscribe } from 'firebase/database';
import { sortBy } from 'lodash';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useSessionContext } from '../authentication';
import { useProjectContext } from '../switch-project';

const InteractionContext = createContext<any>(null);

type FilterTodayOptions = {
  unanswered?: boolean;
};

type FilterSuccessfullyOptions = {
  referenceDate: Date;
};

const InteractionProvider: FC<PropsWithChildren> = ({ children }) => {
  const { userLogged } = useSessionContext();
  const { realtimeDbPath, isCalendarMode, firstCollect, referenceDate } = useProjectContext();

  const [loading, setLoading] = useState<boolean>(false);

  const [contactsPrevMonth, setContactsPrevMonth] = useState<CalendarModel[]>([]);
  const [contactsCurrMonth, setContactsCurrMonth] = useState<CalendarModel[]>([]);
  const [contactsNextMonth, setContactsNextMonth] = useState<CalendarModel[]>([]);

  const [contactsToday, setContactToday] = useState<CalendarModel[]>([]);
  const [contactsTodayTotal, setContactTodayTotal] = useState<number>(0);

  const [deliveredResponseToday, setDeliveredResponseToday] = useState<CalendarModel[]>([]);
  const [deliveredResponseTodayTotal, setDeliveredResponseTodayTotal] = useState<number>(0);

  const [contactsYesterdaySuccessfully, setContactsYesterdaySuccessfully] = useState<CalendarModel[]>([]);
  const [contactsYesterdaySuccessfullyTotal, setContactsYesterdaySuccessfullyTotal] = useState<number>(0);

  const filterToday = (e: CalendarModel, options: FilterTodayOptions) => {
    const conditions: boolean[] = [];

    conditions.push(DateHelper.dateBetween(referenceDate, e.startAt, e.endAt));

    if (firstCollect === 2) {
      conditions.push(e.collectNumber > 1);
    }

    const { unanswered = false } = options;

    if (unanswered) {
      conditions.push(e.status !== StatusCalendar.answered);
    }

    return conditions.every((c: boolean) => c);
  };

  /**
   * Pendente e por dia, nao por evento: a janela dura ate `awaitingDays` dias e
   * cada um tem o seu proprio contato a fazer. `status` continua respondendo
   * pelo ciclo de vida da coleta, e por isso nao entra aqui.
   */
  const isPendingToday = (e: CalendarModel): boolean =>
    CalendarSendHelper.wasSentOn(e, CalendarSendHelper.dayKey(referenceDate)) === false;

  const filterSuccessfully = (e: CalendarModel, options: FilterSuccessfullyOptions) => {
    const { referenceDate } = options;

    const conditions: boolean[] = [
      e.status === StatusCalendar.answered,
      e.answeredAt !== undefined && DateHelper.dateIsEquals(e.answeredAt, referenceDate),
    ];

    conditions.push(e.collectNumber >= firstCollect);

    return conditions.every((c: boolean) => c);
  };

  useEffect(() => {
    const yesterday = DateHelper.subDays(referenceDate, 1);

    const data = [...contactsPrevMonth, ...contactsCurrMonth, ...contactsNextMonth];

    const todayData = data.filter((e: CalendarModel) => filterToday(e, { unanswered: true }));
    setContactToday(todayData);
    setContactTodayTotal(todayData.filter(isPendingToday).length);

    const todaySuccessfullyData = data.filter((e: CalendarModel) => filterSuccessfully(e, { referenceDate }));
    setDeliveredResponseToday(todaySuccessfullyData);
    setDeliveredResponseTodayTotal(
      isCalendarMode ? 0 : todaySuccessfullyData.filter(e => e.gratefulStatus === GratefulStatus.pending).length
    );

    const yesterdaySuccessfullyData = data.filter((e: CalendarModel) =>
      filterSuccessfully(e, { referenceDate: yesterday })
    );

    setContactsYesterdaySuccessfully(yesterdaySuccessfullyData);
    setContactsYesterdaySuccessfullyTotal(
      isCalendarMode ? 0 : yesterdaySuccessfullyData.filter(e => e.gratefulStatus === GratefulStatus.pending).length
    );
  }, [contactsPrevMonth, contactsCurrMonth, contactsNextMonth, referenceDate]);

  useEffect(() => {
    if (realtimeDbPath === false || userLogged === false) return;

    const unsubscribes: Unsubscribe[] = [];

    setLoading(true);

    const prevMonth = DateHelper.subMonth(referenceDate, 1);
    const prevMonthlyFilter = DateHelper.formatDate(prevMonth, 'YYYY-MM');

    const currMonthlyFilter = DateHelper.formatDate(referenceDate, 'YYYY-MM');

    const nextMonth = DateHelper.addMonth(referenceDate, 1);
    const nextMonthlyFilter = DateHelper.formatDate(nextMonth, 'YYYY-MM');

    unsubscribes.push(
      CalendarRepository.watchEvents(
        (rawData: CalendarModel[]) => {
          const data = Object.values(rawData ?? {});
          setContactsPrevMonth(data);
          setLoading(false);
        },
        realtimeDbPath,
        {
          key: 'byMonth',
          value: prevMonthlyFilter,
        }
      )
    );

    unsubscribes.push(
      CalendarRepository.watchEvents(
        (rawData: CalendarModel[]) => {
          const data = Object.values(rawData ?? {});
          setContactsCurrMonth(data);
          setLoading(false);
        },
        realtimeDbPath,
        {
          key: 'byMonth',
          value: currMonthlyFilter,
        }
      )
    );

    unsubscribes.push(
      CalendarRepository.watchEvents(
        (rawData: CalendarModel[]) => {
          const data = Object.values(rawData ?? {});
          setContactsNextMonth(data);
          setLoading(false);
        },
        realtimeDbPath,
        {
          key: 'byMonth',
          value: nextMonthlyFilter,
        }
      )
    );

    return () => unsubscribes.forEach((unsubscribe: Unsubscribe) => unsubscribe());
  }, [realtimeDbPath, referenceDate]);

  return (
    <InteractionContext.Provider
      value={{
        loading,
        contactsTodayTotal,
        contactsToday,
        isPendingToday,
        deliveredResponseTodayTotal: contactsYesterdaySuccessfullyTotal + deliveredResponseTodayTotal,
        deliveredResponseToday: sortBy([...contactsYesterdaySuccessfully, ...deliveredResponseToday], ['id']),
      }}
    >
      {children}
    </InteractionContext.Provider>
  );
};

interface InteractionContextProps {
  loading: string;
  contactsTodayTotal: number;
  contactsToday: CalendarModel[];
  isPendingToday: (event: CalendarModel) => boolean;
  deliveredResponseTodayTotal: number;
  deliveredResponseToday: CalendarModel[];
}

const useInteractionContext = (): InteractionContextProps => useContext(InteractionContext);

export { InteractionProvider, useInteractionContext };
